import moment from 'moment-timezone'
import { pick } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Opportunity, OpportunityPayload } from 'Event/interfaces'
import { FormModal } from 'components/FormModal'
import { EventDateAndRoleScreen, EventDateScreenValues } from 'Event/components/EventDateAndRoleScreen'
import { EventInfoScreen, EventInfoScreenValues } from 'Event/components/EventInfoScreen'
import {
  EventRegistrationDetailsScreen,
  EventRegistrationDetailsScreenValues,
} from 'Event/components/EventRegistrationDetailsScreen'
import {
  EventAutomatedMessagesScreen,
  EventAutomatedMessagesScreenValues,
} from 'Event/components/EventAutomatedMessagesScreen'
import { useGroups } from 'group/hooks/useGroups'
import { Group } from 'Event/components/opportunity/GroupPicker/types'
import { useEnabledGroups } from 'Event/components/opportunity/GroupPicker/hooks'
import { useFeatureEnabled } from 'core/feature/hooks'
import { useGetEventGroups } from 'Event/hooks/useGetEventGroups'
import { useIsTester } from 'auth/hooks'
import Loading from 'civic-champs-shared/core/Loading'
import {
  OpportunityTemplate,
  OpportunityTemplateCredential,
  OpportunityTemplatePersonGroup,
  OpportunityTemplateSurvey,
} from 'volunteering/opportunities/interfaces'
import useGetOpportunityTemplateGroups from 'volunteering/opportunities/hooks/useGetOpportunityTemplateGroups'
import { useRolesCollection } from 'volunteer-role/hooks'
import { Role } from 'volunteer-role/types'
import {
  EventPayload,
  FormData,
  getFormValuesFromOpportunityTemplate,
  getLocationFormValuesFromOpportunityTemplate,
  mapFormDataToOpportunityPayload,
  mapToEventGroup,
  useMapFormDataToEventPayload,
} from 'Event/helpers/add-edit-helpers'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { AssociationType, OpportunityResponseWithRecurrenceInfo } from 'Event/interfaces/interfaceCreateEditEvent'
import useGetEventQuestionSets from 'Event/hooks/useGetEventQuestionSets'
import { encodeOccurrenceFromEvent } from 'Event/helpers/encodeOccurrence'
import { useGetFormDataFromEvent } from '../hooks/useGetFormDataFromEvent'
import { useShowPrompt } from '../../civic-champs-shared/core/modal/hooks'
import EditRecurringEventConfirmDialog, { EditType } from './EditRecurringEventConfirmDialog'
import { RECURRING_TYPE } from '../../civic-champs-shared/core/utils/constants/RECURRING_TYPE_OPTIONS'
import useGetOpportunityTemplateSurveys from 'volunteering/opportunities/hooks/useGetOpportunityTemplateSurveys'
import useGetOpportunityTemplateCredentials from 'volunteering/opportunities/hooks/useGetOpportunityTemplateCredentials'
import { useLocationsCollection } from 'locations/hooks'
import { isSingleEvent } from 'Event/hooks/useIsSingleEvent'

export const AddEditEventPrompt = ({
  showing,
  close,
  complete,
  event,
  initialScreen = 0,
}: {
  showing: boolean
  complete: (value: OpportunityPayload | EventPayload) => void
  close: () => void
  event?: Opportunity | OpportunityResponseWithRecurrenceInfo
  initialScreen?: number
}) => {
  const getFormDataFromEvent = useGetFormDataFromEvent({ useTemplate: true })
  const showEditRecurringPrompt = useShowPrompt(EditRecurringEventConfirmDialog)
  const [isNewEvent, isDraftable] = useMemo(() => {
    if (!event) {
      return [true, true]
    } else {
      return [false, (event as OpportunityResponseWithRecurrenceInfo).schedulable && !event.published]
    }
  }, [event])
  const currentOrg = useCurrentOrg()
  const [screen, setScreen] = useState(initialScreen)

  useEffect(() => {
    if (showing) {
      setScreen(initialScreen)
    }
  }, [initialScreen, showing])
  const { groups: initialGroups, loading: groupsLoading } = useGroups() as { groups: Group[]; loading: boolean }
  const groups = useEnabledGroups(initialGroups)
  const [getEventGroups, { loading: eventGroupsLoading }] = useGetEventGroups()
  const [fetchOpportunityTemplateGroups] = useGetOpportunityTemplateGroups()
  const [fetchOpportunityTemplateSurveys] = useGetOpportunityTemplateSurveys()
  const [fetchOpportunityTemplateCredentials] = useGetOpportunityTemplateCredentials()
  const { loading: isTesterLoading, isTester: isTest } = useIsTester()
  const isQuestionnaireAsSurveyEnabled = useFeatureEnabled('QuestionnaireAsSurvey')
  const isEventAutomatedMessagesEnabled = useFeatureEnabled('EventAutomatedMessages')
  const [{ roles, initiallyLoaded: rolesLoaded }, { addRole }] = useRolesCollection({ reporting: false })
  const titles = useMemo(
    () =>
      isEventAutomatedMessagesEnabled
        ? [(event ? 'Edit' : 'Add') + ' Event', 'Event Information', 'Registration Details', 'Automated Messages']
        : [(event ? 'Edit' : 'Add') + ' Event', 'Event Information', 'Registration Details'],
    [event, isEventAutomatedMessagesEnabled],
  )

  const [{ locations, loading: locationsLoading, initiallyLoaded: locationsInitiallyLoaded }] = useLocationsCollection()
  const namedLocations = useMemo(() => {
    return locations.filter(({ name }) => name !== null)
  }, [locations])

  const [getQuestionSets, { result: questionSets, loading: questionSetsLoading }] = useGetEventQuestionSets()
  const [eventSupplementaryReady, setEventSupplementaryReady] = useState(!event)

  useEffect(() => {
    if (!showing || !event || !locationsInitiallyLoaded) {
      return
    }

    let cancelled = false
    setEventSupplementaryReady(false)

    Promise.all([getQuestionSets(encodeOccurrenceFromEvent(event)), getEventGroups(event.id)])
      .then(([fetchedQuestionSets, eventGroups]) => {
        if (cancelled) {
          return
        }
        setFormData(formData => {
          const data = { ...formData }
          if ((event as OpportunityResponseWithRecurrenceInfo).schedulable) {
            Object.assign(
              data,
              getFormDataFromEvent({
                event,
                questionSets: fetchedQuestionSets,
                isQuestionnaireAsSurveyEnabled,
                namedLocations,
              }),
            )
          }

          const groups = eventGroups.map(mapToEventGroup)
          return {
            ...data,
            groups,
          }
        })
      })
      .finally(() => {
        if (!cancelled) {
          setEventSupplementaryReady(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [showing, locationsInitiallyLoaded, event]) // eslint-disable-line react-hooks/exhaustive-deps

  const initialFormData = useMemo(() => {
    return getFormDataFromEvent({ event, questionSets, isQuestionnaireAsSurveyEnabled, namedLocations })
  }, [event, getFormDataFromEvent, isQuestionnaireAsSurveyEnabled, namedLocations, questionSets])

  const [formData, setFormData] = useState<FormData>(initialFormData)

  useEffect(() => {
    setFormData(data => ({
      ...data,
      isTest,
    }))
  }, [isTest])

  const dateValues: EventDateScreenValues = useMemo(
    () => ({
      ...pick(formData, [
        'startDate',
        'endDate',
        'startTime',
        'endTime',
        'opportunityTemplate',
        'recurringType',
        'roles',
        'isSchedulable',
        'dates',
        'simpleRecurringType',
        'monthlyRecurringType',
        'allowMultipleRegistrations',
      ]),
      useTemplate: isNewEvent,
      saveAsDraft: false,
    }),
    [formData, isNewEvent],
  )
  const eventInformationValues = useMemo(
    () =>
      ({
        name: formData.name,
        description: formData.description,
        address: formData.address || '',
        city: formData.city || '',
        state: formData.state || '',
        zip: formData.zip || '',
        visibility: formData.visibility,
        geofencing: formData.geofencing,
        visibilityGroups: formData.groups.filter(
          ({ associationType }) => associationType === AssociationType.EVENT_PRIVATE_TO_MEMBERS,
        ),
        locationIsAddress: formData.locationIsAddress,
        locationDetails: formData.locationDetails || '',
        contactEmail: formData.contactEmail,
        contactName: formData.contactName,
        contactPhoneNumber: formData.contactPhoneNumber,
        organizationLocationId: formData.organizationLocationId,
        useCustomAddress: formData.useCustomAddress,
        customAddressType: formData.customAddressType,
        plusCode: formData.plusCode || '',
        pointOfContactPersons: formData.pointOfContactPersons,
      } as EventInfoScreenValues),
    [formData],
  )

  const registrationDetailsValues = useMemo(() => {
    const {
      groups,
      questionnaires,
      waivers,
      instructions,
      visibility,
      useCustomPublishTime,
      publishDate,
      publishTime,
    } = formData

    return {
      questionnaires,
      waivers,
      instructions,
      visibility,
      onboardingGroups: groups.filter(
        ({ associationType }) => associationType === AssociationType.ADD_PARTICIPANTS_TO_GROUP,
      ),
      useCustomPublishTime,
      publishDate,
      publishTime,
    }
  }, [formData])

  const handleOpportunitySubmit = useCallback(
    (values: FormData) => {
      complete(mapFormDataToOpportunityPayload(values))
    },
    [complete],
  )

  const mapFormDataToEventPayload = useMapFormDataToEventPayload(roles)

  const handleEventSubmit = useCallback(
    async (values: FormData, asDraft: boolean = false) => {
      let startDateChanged = false
      if (!isDraftable) {
        let editMode: EditType

        const isConcluded = moment(event!.endsAt).isBefore(new Date())

        const oldStartDate = moment.tz(event!.startsAt, currentOrg.timeZone)
        const newStartDate = moment.tz(values.startDate as Date, currentOrg.timeZone)
        startDateChanged = !oldStartDate.isSame(newStartDate, 'day')

        const singleEvent = isSingleEvent(event as OpportunityResponseWithRecurrenceInfo)

        if (isConcluded || startDateChanged || singleEvent) {
          // we edit date of single event, so it's a single edit without prompt
          editMode = EditType.Single
        } else {
          const { editType } = await showEditRecurringPrompt()
          editMode = editType
        }
        if (editMode === 'single') {
          values.recurringType = RECURRING_TYPE.NEVER
        }
        values.editMode = editMode
      }
      complete(mapFormDataToEventPayload({ values, startDateChanged, asDraft }))
    },
    [complete, currentOrg.timeZone, event, isDraftable, mapFormDataToEventPayload, showEditRecurringPrompt],
  )

  const handleSubmit = useCallback(
    (values: Partial<FormData>, asDraft: boolean = false) => {
      const data = {
        ...formData,
        ...values,
      }
      if (data.isSchedulable) {
        return handleEventSubmit(data, asDraft)
      } else {
        return handleOpportunitySubmit(data)
      }
    },
    [formData, handleEventSubmit, handleOpportunitySubmit],
  )

  const handleRegistrationsScreenSubmit = useCallback(
    ({ onboardingGroups, ...values }: EventRegistrationDetailsScreenValues, asDraft: boolean = false) => {
      const data = {
        ...formData,
        ...values,
        groups: [
          ...formData.groups.filter(
            ({ associationType }) => associationType !== AssociationType.ADD_PARTICIPANTS_TO_GROUP,
          ),
          ...onboardingGroups,
        ],
      }

      if (data.isSchedulable && !asDraft && isEventAutomatedMessagesEnabled) {
        setFormData(data)
        setScreen(3)
        return
      }

      return handleSubmit(data, asDraft)
    },
    [formData, handleSubmit, isEventAutomatedMessagesEnabled],
  )

  const automatedMessagesValues = useMemo(
    () => ({
      registrationMessage: formData.registrationMessage || '',
      instructions: formData.instructions || '',
      followUpEnabled: !!formData.followUpEnabled,
      followUpMessage: formData.followUpMessage || '',
      followUpQuestionnaireIds: formData.followUpQuestionnaireIds || [],
    }),
    [formData],
  )

  const handleAutomatedMessagesSubmit = useCallback(
    (values: EventAutomatedMessagesScreenValues, asDraft: boolean = false) => {
      return handleSubmit(values, asDraft)
    },
    [handleSubmit],
  )

  const handleOpportunityTemplateChange = useCallback(
    (opportunityTemplate: OpportunityTemplate | null) => {
      if (opportunityTemplate) {
        setFormData(prev => ({
          ...prev,
          ...getLocationFormValuesFromOpportunityTemplate(opportunityTemplate, namedLocations),
        }))
      }
    },
    [namedLocations],
  )

  const handleDateSubmit = useCallback(
    async (values: EventDateScreenValues) => {
      let opportunityTemplateGroups: OpportunityTemplatePersonGroup[] = []
      let opportunityTemplateSurveys: OpportunityTemplateSurvey[] = []
      let opportunityTemplateCredentials: OpportunityTemplateCredential[] = []
      let fromTemplate: Record<string, any> = {}
      if (values.opportunityTemplate && values.useTemplate) {
        const templateChanged = formData.opportunityTemplate?.id !== values.opportunityTemplate.id
        if (templateChanged) {
          ;[opportunityTemplateGroups, opportunityTemplateSurveys, opportunityTemplateCredentials] = await Promise.all([
            fetchOpportunityTemplateGroups(values.opportunityTemplate.id),
            fetchOpportunityTemplateSurveys(values.opportunityTemplate.id),
            fetchOpportunityTemplateCredentials(values.opportunityTemplate.id),
          ])
          fromTemplate = getFormValuesFromOpportunityTemplate({
            opportunityTemplateGroups,
            opportunityTemplate: values.opportunityTemplate,
            opportunityTemplateSurveys,
            opportunityTemplateCredentials,
            namedLocations,
          })
        } else {
          fromTemplate = getLocationFormValuesFromOpportunityTemplate(values.opportunityTemplate, namedLocations)
        }
      }

      setFormData(data => {
        let publishDefaults: Partial<FormData> = {}

        if (!data.useCustomPublishTime) {
          const { startsAt } = mapFormDataToEventPayload({ values: { ...data, ...values } })
          publishDefaults = {
            publishDate: startsAt,
            publishTime: startsAt,
          }
        }

        return {
          ...data,
          ...values,
          ...publishDefaults,
          ...fromTemplate,
        }
      })
      if (values.saveAsDraft) {
        handleSubmit(values, true)
      } else {
        setScreen(1)
      }
    },
    [
      fetchOpportunityTemplateCredentials,
      fetchOpportunityTemplateGroups,
      fetchOpportunityTemplateSurveys,
      formData.opportunityTemplate,
      handleSubmit,
      mapFormDataToEventPayload,
      namedLocations,
    ],
  )

  const handleInfoSubmit = useCallback(
    ({ visibilityGroups, ...values }: EventInfoScreenValues, asDraft: boolean = false) => {
      setFormData(({ groups, ...data }) => ({
        ...data,
        ...values,
        groups: [
          ...groups.filter(({ associationType }) => associationType !== AssociationType.EVENT_PRIVATE_TO_MEMBERS),
          ...visibilityGroups,
        ],
      }))
      if (asDraft) {
        handleSubmit(values, true)
      } else {
        setScreen(2)
      }
    },
    [handleSubmit],
  )

  const handlePreviousScreen = useCallback((values: Partial<FormData>) => {
    setFormData(data => ({
      ...data,
      ...values,
    }))
    setScreen(value => value - 1)
  }, [])

  const handleAutomatedMessagesBack = useCallback(
    (values: EventAutomatedMessagesScreenValues) => handlePreviousScreen(values),
    [handlePreviousScreen],
  )

  const handleRegistrationsScreenBack = useCallback(
    ({ onboardingGroups, ...values }: EventRegistrationDetailsScreenValues) => {
      return handlePreviousScreen({
        ...values,
        groups: [
          ...formData.groups.filter(
            ({ associationType }) => associationType !== AssociationType.ADD_PARTICIPANTS_TO_GROUP,
          ),
          ...onboardingGroups,
        ],
      })
    },
    [formData.groups, handlePreviousScreen],
  )

  const handleOverviewBack = useCallback(
    (values: EventInfoScreenValues) => {
      return handlePreviousScreen(values)
    },
    [handlePreviousScreen],
  )

  if (
    groupsLoading ||
    eventGroupsLoading ||
    isTesterLoading ||
    !rolesLoaded ||
    questionSetsLoading ||
    locationsLoading ||
    (showing && event && !eventSupplementaryReady)
  ) {
    return <Loading />
  }

  return (
    // draggable={false}: roles list uses react-beautiful-dnd; DraggablePaper breaks it (see FormModal).
    <FormModal id="trip-prompt" showing={showing} close={close} title={titles[screen]} draggable={false}>
      {
        [
          <EventDateAndRoleScreen
            values={dateValues}
            onBack={close}
            isNewEvent={isNewEvent}
            isDraftable={isDraftable}
            onSubmit={handleDateSubmit}
            onOpportunityTemplateChange={handleOpportunityTemplateChange}
            roles={roles as Role[]}
            addRole={addRole as () => Promise<Role>}
          />,
          <EventInfoScreen
            values={eventInformationValues}
            onBack={handleOverviewBack}
            isNewEvent={isNewEvent}
            isDraftable={isDraftable}
            onSubmit={handleInfoSubmit}
            groups={groups}
            isSchedulable={formData.isSchedulable}
            namedLocations={namedLocations}
          />,
          <EventRegistrationDetailsScreen
            values={registrationDetailsValues}
            onBack={handleRegistrationsScreenBack}
            isNewEvent={isNewEvent}
            isDraftable={isDraftable}
            onSubmit={handleRegistrationsScreenSubmit}
            groups={groups}
            isSchedulable={formData.isSchedulable}
            showPublishTime={true}
            showDayOfInstructions={!isEventAutomatedMessagesEnabled}
            nextText={formData.isSchedulable && isEventAutomatedMessagesEnabled ? 'Next' : 'Save'}
          />,
          ...(isEventAutomatedMessagesEnabled
            ? [
                <EventAutomatedMessagesScreen
                  key="automated-messages"
                  values={automatedMessagesValues}
                  onBack={handleAutomatedMessagesBack}
                  isNewEvent={isNewEvent}
                  isDraftable={isDraftable}
                  onSubmit={handleAutomatedMessagesSubmit}
                />,
              ]
            : []),
        ][screen]
      }
    </FormModal>
  )
}

export default AddEditEventPrompt
